import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient, useQuery } from 'react-query';
import { shipmentsAPI, customersAPI, attachmentsAPI, citiesAPI } from '../services/api';
import { Package, ArrowLeft, Save, X, Plus, Trash2, MapPin, Search, User, Paperclip, Upload, File } from 'lucide-react';
import toast from 'react-hot-toast';
import CustomerSearchModal from '../components/CustomerSearchModal';
import AttachmentUpload from '../components/AttachmentUpload';
import CityDropdown from '../components/CityDropdown';
import CustomerSearchDropdown from '../components/CustomerSearchDropdown';

const NewShipment = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    customerId: '',
    pickupAddress: '',
    pickupLocation: '',
    deliveryAddress: '',
    cityId: '',
    dueDate: '',
    deliveryDate: '',
    description: '',
    status: 'inquiry',
    shipmentPaymentStatus: 'pending'
  });
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [boxRequirements, setBoxRequirements] = useState([
    { boxType: '', quantity: 1, customType: '' }
  ]);
  const [errors, setErrors] = useState({});
  const [createdShipmentId, setCreatedShipmentId] = useState(null);
  const [pendingFiles, setPendingFiles] = useState([]);

  // Box type options
  const boxTypeOptions = [
    { value: 'jumbo', label: 'Jumbo' },
    { value: 'medium', label: 'Medium' },
    { value: 'small', label: 'Small' },
    { value: 'custom', label: 'Custom' }
  ];

  // Status options
  const statusOptions = [
    { value: 'inquiry', label: 'Inquiry' },
    { value: 'box_collection', label: 'Box Collection' },
    { value: 'box_delivery', label: 'Box Delivery' },
    { value: 'quick_packing', label: 'Quick Packing' }
  ];

  // Shipment status options
  const shipmentStatusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'in_shipping', label: 'In Shipping' },
    { value: 'in_sl_warehouse', label: 'In SL Warehouse' },
    { value: 'shipping_to_customer', label: 'Shipping to Customer' },
    { value: 'delivered', label: 'Delivered' }
  ];

  // Payment status options
  const paymentStatusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'partial_paid', label: 'Partial Paid' },
    { value: 'fully_paid', label: 'Fully Paid' }
  ];

  const createShipmentMutation = useMutation(
    (data) => shipmentsAPI.create(data),
    {
      onSuccess: (result) => {
        // Upload pending files if any
        if (pendingFiles.length > 0) {
          uploadPendingFiles(result.shipmentId).then(() => {
            queryClient.invalidateQueries('shipments');
            queryClient.invalidateQueries('dashboard-overview');
            toast.success('Shipment created successfully with attachments!');
            navigate('/shipments');
          }).catch(() => {
            queryClient.invalidateQueries('shipments');
            queryClient.invalidateQueries('dashboard-overview');
            toast.success('Shipment created successfully!');
            navigate('/shipments');
          });
        } else {
          queryClient.invalidateQueries('shipments');
          queryClient.invalidateQueries('dashboard-overview');
          toast.success('Shipment created successfully!');
          navigate('/shipments');
        }
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to create shipment');
      }
    }
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleBoxChange = (index, field, value) => {
    const newBoxRequirements = [...boxRequirements];
    newBoxRequirements[index] = {
      ...newBoxRequirements[index],
      [field]: value
    };
    setBoxRequirements(newBoxRequirements);
  };

  const addBoxRequirement = () => {
    setBoxRequirements([...boxRequirements, { boxType: '', quantity: 1, customType: '' }]);
  };

  const removeBoxRequirement = (index) => {
    if (boxRequirements.length > 1) {
      setBoxRequirements(boxRequirements.filter((_, i) => i !== index));
    }
  };

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    setFormData(prev => ({ ...prev, customerId: customer.id }));
    // Clear error when customer is selected
    if (errors.customerId) {
      setErrors(prev => ({
        ...prev,
        customerId: ''
      }));
    }
  };

  const handleFileSelect = (files) => {
    const validFiles = Array.from(files).filter(file => {
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        toast.error(`${file.name} is too large. Maximum size is 10MB.`);
        return false;
      }
      return true;
    });

    if (validFiles.length + pendingFiles.length > 5) {
      toast.error('Maximum 5 files allowed.');
      return;
    }

    setPendingFiles(prev => [...prev, ...validFiles]);
  };

  const removePendingFile = (index) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadPendingFiles = async (shipmentId) => {
    if (pendingFiles.length === 0) return;

    try {
      const result = await attachmentsAPI.upload(shipmentId, pendingFiles);
      toast.success(`${result.message}`);
      setPendingFiles([]);
      return result.attachments;
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to upload files');
      throw error;
    }
  };

  // Utility to normalize pickup location
  function normalizePickupLocation(value) {
    if (!value) return '';
    let loc = value.trim();
    if (loc.startsWith('@')) loc = loc.slice(1);

    // If it's a Google Maps search URL, extract the query param
    const searchUrlMatch = loc.match(/google\.com\/maps\/search\/\?api=1&query=([^&]+)/);
    if (searchUrlMatch) {
      loc = decodeURIComponent(searchUrlMatch[1]);
      if (loc.startsWith('@')) loc = loc.slice(1);
    }
    return loc;
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate form
    const newErrors = {};
    if (!formData.customerId) newErrors.customerId = 'Customer is required';
    if (!formData.cityId) newErrors.cityId = 'City is required';
    if (!formData.dueDate) newErrors.dueDate = 'Due date is required';

    // Validate delivery date cannot be after due date
    if (formData.deliveryDate && formData.dueDate) {
      const deliveryDate = new Date(formData.deliveryDate);
      const dueDate = new Date(formData.dueDate);
      if (deliveryDate > dueDate) {
        newErrors.deliveryDate = 'Delivery date cannot be after due date';
      }
    }

    // Validate box requirements
    const validBoxRequirements = boxRequirements.filter(box => box.boxType && box.quantity > 0);
    if (validBoxRequirements.length === 0) {
      newErrors.boxRequirements = 'At least one box requirement is needed';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Prepare data for API
    const shipmentData = {
      customerId: parseInt(formData.customerId),
      cityId: parseInt(formData.cityId),
      pickupAddress: formData.pickupAddress,
      pickupLocation: normalizePickupLocation(formData.pickupLocation) || null,
      deliveryAddress: formData.deliveryAddress,
      dueDate: formData.dueDate,
      deliveryDate: formData.deliveryDate || null,
      description: formData.description,
      status: formData.status,
      shipmentPaymentStatus: formData.shipmentPaymentStatus,
      boxRequirements: validBoxRequirements.map(box => ({
        boxType: box.boxType === 'custom' ? box.customType : box.boxType,
        quantity: parseInt(box.quantity)
      }))
    };

    createShipmentMutation.mutate(shipmentData);
  };

  const handleFinish = () => {
    navigate('/shipments');
  };

  const handleViewShipment = () => {
    if (createdShipmentId) {
      navigate(`/shipments/${createdShipmentId}`);
    }
  };

  // If shipment was created, show attachment upload
  if (createdShipmentId) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/shipments')}
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Shipments
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Shipment Created!</h1>
              <p className="text-gray-600">Add attachments to your new shipment</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-4">
            <Paperclip className="h-5 w-5 mr-2 text-blue-600" />
            <h3 className="text-lg font-medium text-gray-900">Attachments</h3>
          </div>
          <AttachmentUpload 
            shipmentId={createdShipmentId}
            onUploadSuccess={() => {
              toast.success('Attachments uploaded successfully!');
            }}
          />
          
          <div className="mt-6 flex space-x-4">
            <button
              onClick={handleFinish}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Finish
            </button>
            <button
              onClick={handleViewShipment}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              View Shipment
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/shipments')}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Shipments
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">New Shipment</h1>
            <p className="text-gray-600">Create a new cargo shipment</p>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Customer Information */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <User className="h-5 w-5 mr-2" />
                Customer Information
              </h3>
              <div className="space-y-4">
                <div>
                  <label htmlFor="customerId" className="block text-sm font-medium text-gray-700 mb-2">
                    Customer *
                  </label>
                  <CustomerSearchDropdown
                    value={formData.customerId}
                    onChange={customerId => setFormData(prev => ({ ...prev, customerId }))}
                    placeholder="Select Customer"
                    error={!!errors.customerId}
                  />
                  {errors.customerId && (
                    <p className="mt-1 text-sm text-red-600">{errors.customerId}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Pickup Address
                  </label>
                  <textarea
                    name="pickupAddress"
                    value={formData.pickupAddress}
                    onChange={handleChange}
                    rows={3}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.pickupAddress ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter pickup address"
                  />
                  {errors.pickupAddress && (
                    <p className="mt-1 text-sm text-red-600">{errors.pickupAddress}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <MapPin className="inline h-4 w-4 mr-1" />
                    Pickup Location (Google Maps)
                  </label>
                  <input
                    type="text"
                    name="pickupLocation"
                    value={formData.pickupLocation}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter coordinates (e.g., 23.5880, 58.3829) or Google Maps URL"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Enter coordinates (latitude, longitude) or paste a Google Maps URL to auto-extract coordinates
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Delivery Address
                  </label>
                  <textarea
                    name="deliveryAddress"
                    value={formData.deliveryAddress}
                    onChange={handleChange}
                    rows={3}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.deliveryAddress ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter delivery address"
                  />
                  {errors.deliveryAddress && (
                    <p className="mt-1 text-sm text-red-600">{errors.deliveryAddress}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Shipment Details */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <Package className="h-5 w-5 mr-2" />
                Shipment Details
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    City *
                  </label>
                  <CityDropdown
                    value={formData.cityId}
                    onChange={(cityId) => setFormData(prev => ({ ...prev, cityId }))}
                    placeholder="Select a city"
                    error={!!errors.cityId}
                  />
                  {errors.cityId && (
                    <p className="mt-1 text-sm text-red-600">{errors.cityId}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Delivery Date
                  </label>
                  <input
                    type="date"
                    name="deliveryDate"
                    value={formData.deliveryDate}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.deliveryDate ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.deliveryDate && (
                    <p className="mt-1 text-sm text-red-600">{errors.deliveryDate}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Due Date *
                  </label>
                  <input
                    type="date"
                    name="dueDate"
                    value={formData.dueDate}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.dueDate ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.dueDate && (
                    <p className="mt-1 text-sm text-red-600">{errors.dueDate}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {statusOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Status
                  </label>
                  <select
                    name="shipmentPaymentStatus"
                    value={formData.shipmentPaymentStatus}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.shipmentPaymentStatus ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    {paymentStatusOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter shipment description (optional)"
                  />
                </div>
              </div>
            </div>

            {/* Box Requirements */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <Package className="h-5 w-5 mr-2" />
                  Box Requirements
                </h3>
                <button
                  type="button"
                  onClick={addBoxRequirement}
                  className="flex items-center px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Box
                </button>
              </div>
              
              {errors.boxRequirements && (
                <p className="mb-4 text-sm text-red-600">{errors.boxRequirements}</p>
              )}

              <div className="space-y-4">
                {boxRequirements.map((box, index) => (
                  <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <select
                        value={box.boxType}
                        onChange={(e) => handleBoxChange(index, 'boxType', e.target.value)}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          errors.boxType ? 'border-red-500' : 'border-gray-300'
                        }`}
                      >
                        <option value="">Select box type</option>
                        {boxTypeOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    {box.boxType === 'custom' && (
                      <input
                        type="text"
                        value={box.customType}
                        onChange={(e) => handleBoxChange(index, 'customType', e.target.value)}
                        placeholder="Custom type"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    )}
                    
                    <input
                      type="number"
                      value={box.quantity}
                      onChange={(e) => handleBoxChange(index, 'quantity', parseInt(e.target.value) || 1)}
                      min="1"
                      className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    
                    {boxRequirements.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeBoxRequirement(index)}
                        className="p-2 text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Attachments Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-4">
            <Paperclip className="h-5 w-5 mr-2 text-blue-600" />
            <h3 className="text-lg font-medium text-gray-900">Attachments (Optional)</h3>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Select files to upload after the shipment is created. Maximum 5 files, 10MB each.
          </p>
          
          {/* File Selection Area */}
          <div 
            className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors cursor-pointer"
            onDragOver={(e) => {
              e.preventDefault();
              e.currentTarget.classList.add('border-blue-500', 'bg-blue-50');
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50');
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50');
              const files = e.dataTransfer.files;
              handleFileSelect(files);
            }}
            onClick={() => document.getElementById('file-input')?.click()}
          >
            <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-600 mb-2">
              Drag and drop files here, or <span className="text-blue-600 hover:text-blue-800 font-medium">browse</span>
            </p>
            <p className="text-xs text-gray-500">
              Maximum 5 files, 10MB each. Supported: Images, PDFs, Documents, Archives
            </p>
            <input
              id="file-input"
              type="file"
              multiple
              className="hidden"
              onChange={(e) => handleFileSelect(e.target.files)}
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar"
            />
          </div>

          {/* Selected Files Preview */}
          {pendingFiles.length > 0 && (
            <div className="mt-4 space-y-2">
              <h4 className="text-sm font-medium text-gray-700">Selected Files:</h4>
              {pendingFiles.map((file, index) => (
                <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <div className="flex items-center space-x-2">
                    <File className="h-4 w-4 text-gray-400" />
                    <span className="text-sm">{file.name}</span>
                    <span className="text-xs text-gray-500">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => removePendingFile(index)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Submit Button */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/shipments')}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={createShipmentMutation.isLoading}
            className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {createShipmentMutation.isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Creating...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Create Shipment
              </>
            )}
          </button>
        </div>
      </form>

      {/* Customer Search Modal */}
      <CustomerSearchModal
        isOpen={showCustomerModal}
        onClose={() => setShowCustomerModal(false)}
        onCustomerSelect={handleCustomerSelect}
      />
    </div>
  );
};

export default NewShipment; 